import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { ConnectedFacility, ExpectedExpensesEnum } from '../../../js/api';
import { ConnectionsService } from '../../../js/api';
import type { TravelGuidelines } from '../../../js/types/TravelGuidelines';
import { StatusCodes } from '../constants/workflows';
import { useCodeDefinitionsAsOptions } from '../hooks/useCodeDefinitions';
import {
  CONNECTION_EVENT_HISTORY_QUERY_KEY,
  CONNECTION_QUERY_KEY,
  PROVIDER_CONNECTIONS_LIST_QUERY_KEY,
} from '../pages/Provider/Connections/queryKeys';
import { Button } from './button';
import { Checkbox, CheckboxField, CheckboxGroup } from './checkbox';
import { Label } from './fieldset';
import { TooltippedText } from './tooltipped-text';

const tooltipLinkClass = 'tw:!text-white tw:underline tw:underline-offset-1 hover:tw:opacity-90';

function TravelCapsSection({ travelGuidelines }: { travelGuidelines: TravelGuidelines }) {
  return (
    <div className="tw:mb-4">
      <p className="tw:font-semibold tw:text-brand-green-0 tw:!mb-4 tw:dark:text-brand-green-1">
        Travel Guidelines
        {travelGuidelines?.effective_date && (
          <span className="tw:font-normal tw:text-zinc-500 tw:dark:text-zinc-400">
            {' '}
            (as of {travelGuidelines.effective_date})
          </span>
        )}
      </p>
      <div className="tw:text-sm">
        {travelGuidelines.travel_caps.map((cap) => (
          <div
            key={cap.expense_kind}
            className="tw:flex tw:gap-4 tw:border-b tw:border-zinc-200 tw:py-2 tw:last:border-0 tw:last:pb-0"
          >
            <div className="tw:w-28 tw:shrink-0 tw:text-left">
              <span className="tw:font-bold tw:text-zinc-900 tw:dark:text-zinc-100">
                {cap.expense_kind}
              </span>
            </div>
            <div className="tw:min-w-0 tw:flex-1 tw:text-left tw:space-y-1">
              {cap.caps.map((row, idx) => (
                <div key={idx}>
                  • {row.allowed_amount}
                  {row.type && ` (${row.type})`}
                </div>
              ))}
              {cap.disclaimers && (
                <div className="tw:mt-1 tw:text-xs tw:text-brand-green-0 tw:whitespace-pre-wrap">
                  <i className="far fa-exclamation-circle tw:mr-1" /> {cap.disclaimers}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getProviderSupportTooltipContent(): ReactNode {
  const text = window.SENDIT_CONTACT_INFO.provider_phone_text;
  const voice = window.SENDIT_CONTACT_INFO.provider_phone_voice;
  if (text !== voice) {
    return (
      <>
        If you need to make changes you can{' '}
        <a href={`sms:${text}`} className={tooltipLinkClass}>
          text us at {text}
        </a>{' '}
        or{' '}
        <a href={`tel:${voice}`} className={tooltipLinkClass}>
          call us at {voice}
        </a>
      </>
    );
  }
  return (
    <>
      If you need to make changes you can{' '}
      <a href={`sms:${text}`} className={tooltipLinkClass}>
        text
      </a>{' '}
      or{' '}
      <a href={`tel:${text}`} className={tooltipLinkClass}>
        call
      </a>{' '}
      us at {text}
    </>
  );
}

interface ProviderExpenseAndTermsFormProps {
  connection: ConnectedFacility;
  onUpdate?: () => void;
  className?: string;
}

export function ProviderExpenseAndTermsForm({
  connection,
  onUpdate = () => {},
  className,
}: ProviderExpenseAndTermsFormProps) {
  const [expectedExpenses, setExpectedExpenses] = useState<ExpectedExpensesEnum[]>(
    connection.expected_expenses || []
  );
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const canEditExpenses =
    connection.current_status?.code === StatusCodes.REQUESTED ||
    connection.current_status?.code === StatusCodes.PEND_FAC_REV;

  const updateMutation = useMutation({
    mutationFn: async () => {
      return ConnectionsService.connectionsConnectedFacilityPartialUpdate({
        id: connection.id,
        requestBody: {
          expected_expenses: expectedExpenses,
        },
      });
    },
    onSuccess: async () => {
      setSaveSuccess(true);
      setSaveError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, connection.id] }),
        queryClient.invalidateQueries({ queryKey: [PROVIDER_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, connection.id],
        }),
      ]);
      onUpdate();
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error: unknown) => {
      setSaveError(error instanceof Error ? error.message : 'Failed to update');
      setSaveSuccess(false);
    },
  });

  const handleExpenseChange = (expense: ExpectedExpensesEnum) => {
    if (!canEditExpenses) return;
    setExpectedExpenses((prev) =>
      prev.includes(expense) ? prev.filter((e) => e !== expense) : [...prev, expense]
    );
  };

  const handleSave = () => {
    updateMutation.mutate();
  };

  const { data: expenseOptions = [] } =
    useCodeDefinitionsAsOptions<ExpectedExpensesEnum>('ExpenseCategory');

  const travelGuidelines = connection?.healthcare_entity?.metadata?.travel_guidelines as
    | TravelGuidelines
    | undefined;
  const hasTravelCaps = travelGuidelines?.travel_caps?.length;

  return (
    <div className={`tw:mt-3 ${className || ''}`}>
      <div className="tw:pb-3">
        <div className="tw:space-y-4">
          {hasTravelCaps && <TravelCapsSection travelGuidelines={travelGuidelines} />}

          {/* Expected Expenses - editable when REQUESTED or PEND_FAC_REV */}
          <div>
            <div className="tw:flex tw:flex-col tw:gap-2 tw:italic tw:text-zinc-600 dark:tw:text-zinc-300 tw:mb-2 tw:leading-tight">
              <span>Select any anticipated expenses for assignments at this location</span>
            </div>
            <CheckboxGroup>
              {expenseOptions.map((option) => (
                <CheckboxField key={option.value} className="tw:mb-1">
                  <Checkbox
                    color="brandgreen1"
                    checked={expectedExpenses.includes(option.value)}
                    onChange={() => handleExpenseChange(option.value)}
                    disabled={!canEditExpenses}
                  />
                  <Label>{option.label}</Label>
                </CheckboxField>
              ))}
            </CheckboxGroup>
            {!canEditExpenses && (
              <TooltippedText
                tooltipContent={getProviderSupportTooltipContent()}
                placement="right"
                tooltipClassName="tw:ml-6"
              >
                <span className="tw:text-brand-green-1 tw:italic tw:cursor-pointer tw:text-sm tw:font-medium tw:hover:underline">
                  Need to change?
                </span>
              </TooltippedText>
            )}
          </div>

          {/* Additional Terms - read-only for providers */}
          {connection.additional_terms && (
            <div className="tw:mb-4 tw:pb-3">
              <div className="tw:font-medium tw:text-zinc-700 dark:tw:text-zinc-300 tw:mb-2">
                Additional Terms
              </div>
              <div className="tw:text-zinc-600 dark:tw:text-zinc-400 tw:whitespace-pre-wrap">
                {connection.additional_terms}
              </div>
            </div>
          )}

          {canEditExpenses && (
            <>
              {saveSuccess && (
                <div className="tw:py-2 tw:px-3 tw:bg-green-50 tw:border tw:border-green-200 tw:rounded-lg tw:text-green-800 dark:tw:bg-green-900/20 dark:tw:border-green-800 dark:tw:text-green-200">
                  <i className="fas fa-check-circle tw:mr-2" />
                  Successfully updated!
                </div>
              )}

              {saveError && (
                <div className="tw:py-2 tw:px-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-red-800 dark:tw:bg-red-900/20 dark:tw:border-red-800 dark:tw:text-red-200">
                  <i className="fas fa-exclamation-circle tw:mr-2" />
                  {saveError}
                </div>
              )}

              <div className="tw:flex tw:gap-2">
                <Button
                  color="brandgreen0"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
